import { RECORDING_UPLOAD_MAX_ATTEMPTS } from 'constants';

const DB_NAME = 'hrms_recording_uploads';
const STORE_NAME = 'pending_uploads';
const DB_VERSION = 1;

const openDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB unavailable'));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
};

export const enqueueRecordingUpload = async (entry) => {
  await withStore('readwrite', (store) => {
    store.put({
      ...entry,
      attempts: entry.attempts || 0,
      createdAt: entry.createdAt || Date.now(),
    });
  });
};

export const removeRecordingUpload = async (id) => {
  await withStore('readwrite', (store) => {
    store.delete(id);
  });
};

export const listPendingRecordingUploads = async () => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const rows = (request.result || []).sort((a, b) => a.createdAt - b.createdAt);
      resolve(rows);
    };
    request.onerror = () => reject(request.error);
  });
};

export const bumpRecordingUploadAttempt = async (id) => {
  const rows = await listPendingRecordingUploads();
  const row = rows.find((item) => item.id === id);
  if (!row) {
    return null;
  }
  const updated = {
    ...row,
    attempts: (row.attempts || 0) + 1,
    lastAttemptAt: Date.now(),
  };
  await enqueueRecordingUpload(updated);
  return updated;
};

export const processRecordingUploadQueue = async (uploadFn) => {
  const pending = await listPendingRecordingUploads();
  const results = [];

  for (const item of pending) {
    if ((item.attempts || 0) >= RECORDING_UPLOAD_MAX_ATTEMPTS) {
      results.push({ id: item.id, status: 'failed' });
      continue;
    }

    try {
      const blob = new Blob([item.blobBuffer], { type: item.mimeType });
      const formData = new FormData();
      const extension = item.mimeType.includes('mp4') ? 'mp4' : 'webm';
      const label = item.recordingMode === 'continuous'
        ? `continuous-${item.partIndex}`
        : `recording-${item.slotAt}`;
      const file = new File([blob], `${label}.${extension}`, { type: item.mimeType.split(';')[0] });
      formData.append('recording', file);
      formData.append('wfhId', item.wfhId);
      formData.append('captureSessionId', item.captureSessionId);
      formData.append('recordedAt', item.recordedAt);
      formData.append('durationMs', String(item.durationMs));
      formData.append('recordingMode', item.recordingMode);
      if (item.recordingMode === 'continuous') {
        formData.append('partIndex', String(item.partIndex));
      }

      await uploadFn(formData);
      await removeRecordingUpload(item.id);
      results.push({ id: item.id, status: 'uploaded' });
    } catch (err) {
      await bumpRecordingUploadAttempt(item.id);
      results.push({ id: item.id, status: 'retry' });
    }
  }

  return results;
};

export const buildUploadQueueId = ({
  wfhId,
  recordingMode,
  slotAt,
  partIndex,
}) => {
  if (recordingMode === 'continuous') {
    return `${wfhId}:continuous:${partIndex}`;
  }
  return `${wfhId}:interval:${slotAt}`;
};

export const bufferFromBlob = async (blob) => {
  const buffer = await blob.arrayBuffer();
  return buffer;
};
